import { useCallback, useEffect, useState } from "react";
import { Plus, TicketPercent, Pencil, Trash2 } from "lucide-react";
import {
  Button, Input, Select, Label, FieldHint, Modal, ModalFooter, Badge,
  EmptyState, Skeleton, useToast, useConfirm,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
  storeApi, centsToStr, strToCents,
  type StoreDiscount, type StoreProduct, type StoreCollection,
} from "./api";

function discountSummary(d: StoreDiscount): string {
  if (d.kind === "percent") return `${d.value}% off`;
  if (d.kind === "fixed") return `${formatMoney(d.value)} off`;
  return "Free shipping";
}

function isLive(d: StoreDiscount): boolean {
  const now = Date.now();
  if (!d.active) return false;
  if (d.starts_at && new Date(d.starts_at).getTime() > now) return false;
  if (d.ends_at && new Date(d.ends_at).getTime() < now) return false;
  if (d.max_uses != null && d.used_count >= d.max_uses) return false;
  return true;
}

export function StoreDiscountsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [discounts, setDiscounts] = useState<StoreDiscount[] | null>(null);
  const [editing, setEditing] = useState<StoreDiscount | "new" | null>(null);

  const load = useCallback(() => {
    storeApi.discounts()
      .then((r) => setDiscounts(r.discounts))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load discounts"));
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (d: StoreDiscount) => {
    if (!(await confirm({ title: `Delete code ${d.code}?`, description: "Shoppers won't be able to use it anymore.", confirmLabel: "Delete", danger: true }))) return;
    try { await storeApi.deleteDiscount(d.id); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : "Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Discounts</h1>
          <p className="mt-1 text-sm text-zinc-400">Promo codes shoppers can apply at checkout.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New code</Button>
      </div>

      {discounts === null ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : discounts.length === 0 ? (
        <EmptyState
          icon={TicketPercent}
          title="No discount codes yet"
          description="Create a code like WELCOME10 to run your first promotion."
          action={<Button onClick={() => setEditing("new")}>New code</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Code</th>
                <th className="px-4 py-2.5 font-medium">Discount</th>
                <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Used</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {discounts.map((d) => (
                <tr key={d.id} className="cursor-pointer bg-zinc-950 transition hover:bg-zinc-900/50" onClick={() => setEditing(d)}>
                  <td className="px-4 py-3 font-mono font-medium text-zinc-100">{d.code}</td>
                  <td className="px-4 py-3 text-zinc-300">
                    {discountSummary(d)}
                    {d.min_subtotal_cents != null && <span className="text-xs text-zinc-500"> · min {formatMoney(d.min_subtotal_cents)}</span>}
                    {d.applies_to !== "all" && <span className="text-xs text-zinc-500"> · limited scope</span>}
                  </td>
                  <td className="hidden px-4 py-3 text-zinc-400 sm:table-cell">
                    {d.used_count}{d.max_uses != null ? ` / ${d.max_uses}` : ""}
                  </td>
                  <td className="px-4 py-3">
                    {isLive(d) ? <Badge color="emerald" size="sm">Live</Badge> : <Badge color="zinc" size="sm">Inactive</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditing(d); }} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil className="h-4 w-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); void remove(d); }} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <DiscountEditor
          discount={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function DiscountEditor({ discount, onClose, onSaved }: {
  discount: StoreDiscount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [collections, setCollections] = useState<StoreCollection[]>([]);
  const [form, setForm] = useState({
    code: discount?.code ?? "",
    kind: discount?.kind ?? "percent",
    percent: discount?.kind === "percent" ? String(discount.value) : "10",
    fixed: discount?.kind === "fixed" ? centsToStr(discount.value) : "",
    applies_to: discount?.applies_to ?? "all",
    applies_ids: discount?.applies_ids ?? [],
    min_subtotal: discount?.min_subtotal_cents != null ? centsToStr(discount.min_subtotal_cents) : "",
    starts_at: toLocalInput(discount?.starts_at ?? null),
    ends_at: toLocalInput(discount?.ends_at ?? null),
    max_uses: discount?.max_uses != null ? String(discount.max_uses) : "",
    max_uses_per_customer: discount?.max_uses_per_customer != null ? String(discount.max_uses_per_customer) : "",
    active: discount?.active ?? true,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.applies_to === "products") storeApi.products().then((r) => setProducts(r.products)).catch(() => {});
    if (form.applies_to === "collections") storeApi.collections().then((r) => setCollections(r.collections)).catch(() => {});
  }, [form.applies_to]);

  const save = async () => {
    const code = form.code.trim().toUpperCase();
    if (code.length < 2) { toast.error("Please enter a code (like WELCOME10)."); return; }
    const body = {
      code,
      kind: form.kind as StoreDiscount["kind"],
      value: form.kind === "percent" ? Number(form.percent) || 0
        : form.kind === "fixed" ? strToCents(form.fixed) : 0,
      applies_to: form.applies_to as StoreDiscount["applies_to"],
      applies_ids: form.applies_to === "all" ? [] : form.applies_ids,
      min_subtotal_cents: form.min_subtotal ? strToCents(form.min_subtotal) : null,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      max_uses_per_customer: form.max_uses_per_customer ? Number(form.max_uses_per_customer) : null,
      active: form.active,
    };
    if (body.kind === "percent" && (body.value < 1 || body.value > 100)) {
      toast.error("Percent must be between 1 and 100.");
      return;
    }
    setSaving(true);
    try {
      if (discount) await storeApi.updateDiscount(discount.id, body);
      else await storeApi.createDiscount(body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const scopeItems: { id: string; name: string }[] =
    form.applies_to === "products" ? products : form.applies_to === "collections" ? collections : [];

  return (
    <Modal open onClose={onClose} title={discount ? `Edit ${discount.code}` : "New discount code"} size="md">
      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="d-code">Code</Label>
            <Input id="d-code" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="WELCOME10" className="font-mono" />
          </div>
          <div>
            <Label htmlFor="d-kind">Type</Label>
            <Select id="d-kind" value={form.kind} onChange={(e) => set("kind", e.target.value as typeof form.kind)}>
              <option value="percent">Percent off</option>
              <option value="fixed">Fixed amount off</option>
              <option value="free_shipping">Free shipping</option>
            </Select>
          </div>
        </div>

        {form.kind === "percent" && (
          <div className="w-36">
            <Label htmlFor="d-pct">Percent off</Label>
            <Input id="d-pct" value={form.percent} onChange={(e) => set("percent", e.target.value)} inputMode="numeric" />
          </div>
        )}
        {form.kind === "fixed" && (
          <div className="w-36">
            <Label htmlFor="d-fix">Amount off ($)</Label>
            <Input id="d-fix" value={form.fixed} onChange={(e) => set("fixed", e.target.value)} inputMode="decimal" />
          </div>
        )}

        <div>
          <Label htmlFor="d-scope">Applies to</Label>
          <Select id="d-scope" value={form.applies_to} onChange={(e) => { set("applies_to", e.target.value as typeof form.applies_to); set("applies_ids", []); }}>
            <option value="all">Entire order</option>
            <option value="products">Specific products</option>
            <option value="collections">Specific collections</option>
          </Select>
          {form.applies_to !== "all" && (
            <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-zinc-800 p-2">
              {scopeItems.length === 0 ? (
                <p className="p-1 text-xs text-zinc-500">Nothing to choose from yet.</p>
              ) : scopeItems.map((item) => (
                <label key={item.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-zinc-900">
                  <input
                    type="checkbox"
                    checked={form.applies_ids.includes(item.id)}
                    onChange={(e) => set("applies_ids", e.target.checked
                      ? [...form.applies_ids, item.id]
                      : form.applies_ids.filter((x) => x !== item.id))}
                    className="accent-rose-500"
                  />
                  <span className="truncate text-zinc-200">{item.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="d-min">Minimum order ($)</Label>
            <Input id="d-min" value={form.min_subtotal} onChange={(e) => set("min_subtotal", e.target.value)} inputMode="decimal" placeholder="none" />
          </div>
          <div>
            <Label htmlFor="d-max">Total uses limit</Label>
            <Input id="d-max" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} inputMode="numeric" placeholder="unlimited" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="d-start">Starts</Label>
            <Input id="d-start" type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="d-end">Ends</Label>
            <Input id="d-end" type="datetime-local" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} />
          </div>
        </div>
        <div className="w-48">
          <Label htmlFor="d-percust">Uses per customer</Label>
          <Input id="d-percust" value={form.max_uses_per_customer} onChange={(e) => set("max_uses_per_customer", e.target.value)} inputMode="numeric" placeholder="unlimited" />
          <FieldHint>Matched by the shopper's email.</FieldHint>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-rose-500" />
          Active
        </label>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
